import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Trash2, Image as ImageIcon, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { AdminVendor, VendorFormData } from '../../types/admin';

interface Props {
  vendor?: AdminVendor | null;
  onClose: () => void;
  onSubmit: (data: VendorFormData) => Promise<void>;
}

export default function VendorFormModal({ vendor, onClose, onSubmit }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<VendorFormData>({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    business_license: '',
    service_categories: [],
    pricing_structure: {
      min_price: 0,
      max_price: 0,
      unit: 'per event',
      currency: 'LKR'
    },
    availability: {
      days: [],
      hours: ''
    },
    payment_terms: '',
    contract_details: '',
    photos: []
  });

  const serviceCategories = [
    'Photography', 'Videography', 'Catering', 'Music & DJ', 'Flowers & Decor',
    'Transportation', 'Makeup & Beauty', 'Wedding Planning', 'Lighting', 'Security'
  ];

  const weekDays = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name,
        contact_person: vendor.contact_person,
        email: vendor.email,
        phone: vendor.phone,
        address: vendor.address,
        website: vendor.website || '',
        business_license: vendor.business_license || '',
        service_categories: vendor.service_categories,
        pricing_structure: vendor.pricing_structure as any,
        availability: vendor.availability as any,
        payment_terms: vendor.payment_terms || '',
        contract_details: vendor.contract_details || '',
        photos: vendor.photos
      });
    }
  }, [vendor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      await onSubmit(formData);
    } catch (err) {
      console.error('Error submitting vendor form:', err);
      setError('Failed to save vendor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryToggle = (category: string) => {
    setFormData(prev => ({
      ...prev,
      service_categories: prev.service_categories.includes(category)
        ? prev.service_categories.filter(c => c !== category)
        : [...prev.service_categories, category]
    }));
  };

  const handleDayToggle = (day: string) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        days: prev.availability.days.includes(day)
          ? prev.availability.days.filter(d => d !== day)
          : [...prev.availability.days, day]
      }
    }));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingPhoto(true);
      const uploadedUrls: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `vendors/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('vendor-venue-images')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('vendor-venue-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...uploadedUrls]
      }));
    } catch (err) {
      console.error('Error uploading photos:', err);
      setError('Failed to upload photos. Please try again.');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50">
      <div className="card-pastel p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800 font-poppins">
            {vendor ? 'Edit Vendor' : 'Add New Vendor'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/60 rounded-full transition-colors text-gray-500 hover:text-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 text-red-600 p-3 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-800 font-poppins">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Vendor Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-pastel"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Contact Person *
                </label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className="input-pastel"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-pastel"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-pastel"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Business Address *
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input-pastel"
                  rows={2}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="input-pastel"
                  placeholder="https://"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Business License Number
                </label>
                <input
                  type="text"
                  value={formData.business_license}
                  onChange={(e) => setFormData({ ...formData, business_license: e.target.value })}
                  className="input-pastel"
                />
              </div>
            </div>
          </div>

          {/* Service Categories */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-800 font-poppins">Service Categories *</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {serviceCategories.map(category => (
                <label key={category} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.service_categories.includes(category)}
                    onChange={() => handleCategoryToggle(category)}
                    className="h-4 w-4 bg-white/60 border-pastel-purple-200 rounded text-pastel-purple-600 focus:ring-pastel-purple-400"
                  />
                  <span className="text-gray-600 text-sm">{category}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Pricing Structure */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-800 font-poppins">Pricing Structure</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Min Price
                </label>
                <input
                  type="number"
                  value={formData.pricing_structure.min_price === 0 ? '' : formData.pricing_structure.min_price}
                  onChange={(e) => setFormData({
                    ...formData,
                    pricing_structure: {
                      ...formData.pricing_structure,
                      min_price: e.target.value === '' ? 0 : Number(e.target.value)
                    }
                  })}
                  className="input-pastel"
                  min="0"
                  placeholder="Enter minimum price"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Max Price
                </label>
                <input
                  type="number"
                  value={formData.pricing_structure.max_price === 0 ? '' : formData.pricing_structure.max_price}
                  onChange={(e) => setFormData({
                    ...formData,
                    pricing_structure: {
                      ...formData.pricing_structure,
                      max_price: e.target.value === '' ? 0 : Number(e.target.value)
                    }
                  })}
                  className="input-pastel"
                  min="0"
                  placeholder="Enter maximum price"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Unit
                </label>
                <select
                  value={formData.pricing_structure.unit}
                  onChange={(e) => setFormData({
                    ...formData,
                    pricing_structure: {
                      ...formData.pricing_structure,
                      unit: e.target.value
                    }
                  })}
                  className="input-pastel"
                >
                  <option value="per event">Per Event</option>
                  <option value="per hour">Per Hour</option>
                  <option value="per day">Per Day</option>
                  <option value="per person">Per Person</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Currency
                </label>
                <select
                  value={formData.pricing_structure.currency}
                  onChange={(e) => setFormData({
                    ...formData,
                    pricing_structure: {
                      ...formData.pricing_structure,
                      currency: e.target.value
                    }
                  })}
                  className="input-pastel"
                >
                  <option value="LKR">LKR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>
          </div>

          {/* Availability */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-800 font-poppins">Availability</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Available Days
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {weekDays.map(day => (
                    <label key={day} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.availability.days.includes(day)}
                        onChange={() => handleDayToggle(day)}
                        className="h-4 w-4 bg-white/60 border-pastel-purple-200 rounded text-pastel-purple-600 focus:ring-pastel-purple-400"
                      />
                      <span className="text-gray-600 text-sm">{day}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Working Hours
                </label>
                <input
                  type="text"
                  value={formData.availability.hours}
                  onChange={(e) => setFormData({
                    ...formData,
                    availability: {
                      ...formData.availability,
                      hours: e.target.value
                    }
                  })}
                  className="input-pastel"
                  placeholder="e.g., 9:00 AM - 6:00 PM"
                />
              </div>
            </div>
          </div>

          {/* Contract & Payment */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-800 font-poppins">Contract & Payment</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Payment Terms
                </label>
                <textarea
                  value={formData.payment_terms}
                  onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  className="input-pastel"
                  rows={3}
                  placeholder="e.g., 50% deposit, 50% on completion"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Contract Details
                </label>
                <textarea
                  value={formData.contract_details}
                  onChange={(e) => setFormData({ ...formData, contract_details: e.target.value })}
                  className="input-pastel"
                  rows={3}
                  placeholder="Additional contract terms and conditions"
                />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-800 font-poppins">Photos</h4>
              <div className="flex items-center space-x-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="btn-pastel flex items-center space-x-2 px-3 py-1 text-sm disabled:opacity-50"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  <span>{uploadingPhoto ? 'Uploading...' : 'Upload Photos'}</span>
                </button>
              </div>
            </div>
            {formData.photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.photos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={photo}
                      alt={`Vendor photo ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-pastel-purple-200 rounded-lg p-8 text-center cursor-pointer hover:border-pastel-purple-400 hover:bg-white/40 transition-colors"
              >
                <ImageIcon className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Click to upload photos</p>
                <p className="text-gray-400 text-xs mt-1">PNG, JPG up to 5MB each</p>
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-pastel-purple-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || formData.service_categories.length === 0}
              className="btn-pastel px-6 py-2 disabled:opacity-50"
            >
              {loading ? 'Saving...' : vendor ? 'Update Vendor' : 'Create Vendor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
